import { Search } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PresetKeywordButtons } from './PresetKeywordButtons'
import { NewsGrid } from './NewsGrid'
import { AISummaryCard } from './AISummaryCard'
import { AISummaryQuota } from './AISummaryQuota'
import { ShareClipButton } from './ShareClipButton'
import { useNewsStore } from '../stores/newsStore'
import type { StreamingSummary } from '../hooks/useStreamingSummary'
import { formatCount } from '@/lib/utils'

interface MainContentProps {
  onPresetSelect: (keywords: string[]) => void
  onPageChange: (keyword: string, page: number) => void
  /**
   * Shared streaming state owned by App. Do not call `useStreamingSummary()` here —
   * the hook keeps its state locally, so a second instance would render an empty
   * summary while App's instance silently burns OpenAI tokens and user credits.
   */
  streaming: StreamingSummary
}

/**
 * Main content area displaying search results
 * Shows empty state or keyword tabs with news results
 */
export function MainContent({ onPresetSelect, onPageChange, streaming }: MainContentProps) {
  const { keywords, activeKeyword, tabs, settings, setActiveKeyword } = useNewsStore()
  const { getSummary, isStreaming, error, cancelStreaming } = streaming

  // Empty state when no keywords or tabs
  if (keywords.length === 0 || Object.keys(tabs).length === 0) {
    return (
      <div className="p-6">
        <div className="flex min-h-[600px] flex-col items-center justify-center gap-4">
          <div className="rounded-full bg-muted p-6">
            <Search className="h-16 w-16 text-muted-foreground" />
          </div>
          <div className="text-center">
            <h2 className="mb-2 text-2xl font-semibold">검색을 시작하세요</h2>
            <p className="text-muted-foreground">키워드를 입력하고 검색 버튼을 클릭하세요</p>
          </div>
          <PresetKeywordButtons onSelectPreset={onPresetSelect} />
        </div>
      </div>
    )
  }

  // Results view with tabs
  return (
    <div className="p-6">
      <Tabs
        value={activeKeyword || keywords[0]}
        onValueChange={setActiveKeyword}
        className="w-full"
      >
        {/*
          Keyword tabs. On narrow screens 20 wrapped tabs became 4–6 rows of chrome
          above the results, so they scroll horizontally instead and only wrap once
          there is room. `-mx-*`/`px-*` lets the scroll area bleed to the edges while
          the first and last tab keep their padding.
        */}
        <div className="-mx-4 mb-6 overflow-x-auto px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:overflow-visible lg:px-0">
          <TabsList className="inline-flex h-auto w-max snap-x gap-1 lg:w-auto lg:flex-wrap">
            {keywords.map((keyword) => (
              <TabsTrigger
                key={keyword}
                value={keyword}
                className="min-h-11 shrink-0 snap-start px-4 py-2 lg:min-h-0"
              >
                {keyword}
                {tabs[keyword] && (
                  <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs">
                    {formatCount(tabs[keyword].total || 0)}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* Tab Content */}
        {keywords.map((keyword) => (
          <TabsContent key={keyword} value={keyword} className="mt-0">
            {/* AI Summary Section */}
            {keyword === activeKeyword &&
              !tabs[keyword]?.isLoading &&
              tabs[keyword]?.items &&
              tabs[keyword].items.length > 0 && (
                <div className="mb-6">
                  {settings.enableAISummary ? (
                    <>
                      {(isStreaming(keyword) || getSummary(keyword) || error) && (
                        <AISummaryCard
                          keyword={keyword}
                          summary={
                            !isStreaming(keyword) && getSummary(keyword)
                              ? {
                                  keyword,
                                  summary_text: getSummary(keyword),
                                  article_count: tabs[activeKeyword]?.items.length || 0,
                                  duplicate_count: 0,
                                }
                              : undefined
                          }
                          isStreaming={isStreaming(keyword)}
                          streamingText={getSummary(keyword)}
                          onCancelStreaming={cancelStreaming}
                        />
                      )}
                      {/*
                        Outside the card's condition on purpose: the card renders
                        nothing until a summary exists, and the count is most useful
                        before spending one.
                      */}
                      <AISummaryQuota />
                    </>
                  ) : (
                    <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
                      <AlertDescription className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>💡</span>
                        <span>
                          <strong>AI 요약</strong> 기능을 검색 설정에서 활성화하면 뉴스를 자동으로
                          요약해드립니다.
                        </span>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

            {/* Share the whole tab as a clipping on our own domain */}
            {(tabs[keyword]?.items?.length ?? 0) > 0 && (
              <div className="mb-4 flex justify-end">
                <ShareClipButton
                  keyword={keyword}
                  items={tabs[keyword].items}
                  summary={getSummary(keyword) || undefined}
                />
              </div>
            )}

            {/* News Grid */}
            <NewsGrid
              items={tabs[keyword]?.items || []}
              isLoading={tabs[keyword]?.isLoading}
              error={tabs[keyword]?.error}
              keyword={keyword}
              total={tabs[keyword]?.total || 0}
              currentPage={tabs[keyword]?.currentPage || 1}
              itemsPerPage={settings.display}
              onPageChange={(page) => onPageChange(keyword, page)}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
